import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, X, Loader2, Calendar, Hash, Building2 } from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { ToastModal } from '../../components/ui/Toast';
import { documentAPI, carAPI } from '../../services/api';
import { formatVehicleName } from '../../lib/utils';

const DOCUMENT_TYPES = [
  { value: 'insurance', label: 'Insurance' },
  { value: 'registration', label: 'Registration (RC)' },
  { value: 'pollution', label: 'Pollution Certificate (PUC)' },
  { value: 'fitness', label: 'Fitness Certificate' },
  { value: 'permit', label: 'Permit' },
  { value: 'other', label: 'Other' }
];

const DocumentUpload = () => {
  const { id: carId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [carLoading, setCarLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);

  const [form, setForm] = useState({
    documentType: '',
    documentNumber: '',
    issueDate: '',
    expiryDate: '',
    issuingAuthority: '',
    notes: ''
  });

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const res = await carAPI.getById(carId);
        setCar(res.data.car || res.data);
      } catch (err) {
        setToast({ type: 'error', message: 'Failed to load car details' });
      } finally {
        setCarLoading(false);
      }
    };
    fetchCar();
  }, [carId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setToast({ type: 'error', message: 'File size must be less than 10MB' });
      return;
    }

    setSelectedFile(file);

    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setToast({ type: 'error', message: 'Please select a document file' });
      return;
    }
    if (!form.documentType) {
      setToast({ type: 'error', message: 'Please select document type' });
      return;
    }
    if (!form.documentNumber) {
      setToast({ type: 'error', message: 'Please enter document number' });
      return;
    }
    if (!form.issueDate || !form.expiryDate) {
      setToast({ type: 'error', message: 'Please enter issue and expiry dates' });
      return;
    }
    if (!form.issuingAuthority) {
      setToast({ type: 'error', message: 'Please enter issuing authority' });
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('document', selectedFile);
      formData.append('carId', carId);
      formData.append('documentType', form.documentType);
      formData.append('documentNumber', form.documentNumber);
      formData.append('issueDate', form.issueDate);
      formData.append('expiryDate', form.expiryDate);
      formData.append('issuingAuthority', form.issuingAuthority);
      if (form.notes) formData.append('notes', form.notes);

      await documentAPI.uploadDocument(formData);
      setToast({ type: 'success', message: 'Document uploaded successfully!' });
      setTimeout(() => navigate(`/cars/${carId}`), 1500);
    } catch (err) {
      setToast({
        type: 'error',
        message: err.response?.data?.message || 'Failed to upload document'
      });
    } finally {
      setLoading(false);
    }
  };

  const carTitle = car ? formatVehicleName(car, { showReg: true }) : '';

  return (
    <div className="space-y-6">
      <PageHeader
        title="Upload Document"
        subtitle={carLoading ? 'Loading...' : carTitle}
        action={
          <Button variant="outline" onClick={() => navigate(`/cars/${carId}`)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Car
          </Button>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* File Upload Section */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Document File
              </h3>

              {!selectedFile ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center cursor-pointer hover:border-indigo-400 hover:bg-indigo-50/50 transition-all"
                >
                  <Upload className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-sm font-medium text-gray-700">Click to upload document</p>
                  <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG up to 10MB</p>
                </div>
              ) : (
                <div className="border border-gray-200 rounded-xl p-4">
                  {preview ? (
                    <img src={preview} alt="Preview" className="w-full h-48 object-contain rounded-lg mb-3" />
                  ) : (
                    <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center mb-3">
                      <FileText className="h-16 w-16 text-gray-400" />
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{selectedFile.name}</p>
                      <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <button type="button" onClick={removeFile} className="p-1 text-red-500 hover:bg-red-50 rounded-full">
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                onChange={handleFileSelect}
                className="hidden"
              />
            </CardContent>
          </Card>

          {/* Document Details */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
                  Document Details
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Select
                    label="Document Type *"
                    name="documentType"
                    value={form.documentType}
                    onChange={handleChange}
                    options={[
                      { value: '', label: 'Select type...' },
                      ...DOCUMENT_TYPES
                    ]}
                  />

                  <Input
                    label="Document Number *"
                    name="documentNumber"
                    value={form.documentNumber}
                    onChange={handleChange}
                    placeholder="e.g. POL-KA01-2026-12345"
                    icon={Hash}
                  />

                  <Input
                    label="Issue Date *"
                    name="issueDate"
                    type="date"
                    value={form.issueDate}
                    onChange={handleChange}
                    icon={Calendar}
                  />

                  <Input
                    label="Expiry Date *"
                    name="expiryDate"
                    type="date"
                    value={form.expiryDate}
                    onChange={handleChange}
                    icon={Calendar}
                  />

                  <Input
                    label="Issuing Authority *"
                    name="issuingAuthority"
                    value={form.issuingAuthority}
                    onChange={handleChange}
                    placeholder="e.g. RTO Bangalore"
                    icon={Building2}
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    placeholder="Any additional notes..."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => navigate(`/cars/${carId}`)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Document
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </form>

      {toast && (
        <ToastModal
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default DocumentUpload;
